<template>
  <li class="nav-item nav-item-action">
    <a
      v-if="isExternal"
      class="nav-action"
      :class="{
        'nav-action-primary': variant === 'primary',
        'nav-action-secondary': variant === 'secondary',
        'nav-action-outline': variant === 'outline'
      }"
      :href="href"
      :target="'_blank'"
      :rel="'noopener noreferrer'"
      @click="$emit('click')"
    >
      <i v-if="icon" :class="icon" class="nav-action-icon"></i>
      <span class="nav-action-text">{{ label }}</span>
      <i v-if="isExternal" class="fas fa-external-link-alt nav-action-external"></i>
    </a>
    <a
      v-else-if="href && isFileDownload"
      class="nav-action"
      :class="{
        'nav-action-primary': variant === 'primary',
        'nav-action-secondary': variant === 'secondary',
        'nav-action-outline': variant === 'outline'
      }"
      :href="href"
      :download="isFileDownload"
      @click="$emit('click')"
    >
      <i v-if="icon" :class="icon" class="nav-action-icon"></i>
      <span class="nav-action-text">{{ label }}</span>
    </a>
    <a
      v-else-if="href"
      class="nav-action"
      :class="{
        'nav-action-primary': variant === 'primary',
        'nav-action-secondary': variant === 'secondary',
        'nav-action-outline': variant === 'outline'
      }"
      :href="href"
      @click.prevent="$emit('click')"
    >
      <i v-if="icon" :class="icon" class="nav-action-icon"></i>
      <span class="nav-action-text">{{ label }}</span>
    </a>
  </li>
</template>

<script>
export default {
  name: 'NavAction',
  props: {
    label: {
      type: String,
      required: true
    },
    href: {
      type: String,
      default: null
    },
    to: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: 'outline',
      validator: (value) => ['primary', 'secondary', 'outline'].includes(value)
    }
  },
  computed: {
    isExternal() {
      if (!this.href) return false
      return this.href.startsWith('http://') || this.href.startsWith('https://')
    },
    isFileDownload() {
      if (!this.href) return false
      // Estensioni di file comuni che dovrebbero essere scaricati
      const fileExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.zip', '.rar', '.png', '.jpg', '.jpeg', '.gif', '.svg']
      return fileExtensions.some(ext => this.href.toLowerCase().endsWith(ext))
    }
  },
  emits: ['click']
}
</script>

<style scoped>
.nav-item-action {
  position: relative;
}

.nav-action {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1.5rem;
  text-decoration: none;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  margin: 0.125rem 0;
}

.nav-action::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s ease, height 0.6s ease;
  z-index: 0;
}

.nav-action-outline:hover::before {
  display: none;
}

.nav-action:not(.nav-action-outline):hover::before {
  width: 300px;
  height: 300px;
}

.nav-action-icon,
.nav-action-text,
.nav-action-external {
  position: relative;
  z-index: 1;
}

.nav-action-icon {
  font-size: 0.875rem;
}

.nav-action-external {
  font-size: 0.7rem;
  opacity: 0.8;
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.nav-action:hover .nav-action-external {
  opacity: 1;
  transform: translate(2px, -2px);
}

/* Outline variant (default) */
.nav-action-outline {
  color: white;
  border: 1.5px solid rgba(255, 255, 255, 0.4);
  background-color: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.nav-action-outline:hover {
  color: #2a5298 !important;
  background-color: white;
  border-color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.nav-action-outline:hover .nav-action-text,
.nav-action-outline:hover .nav-action-icon,
.nav-action-outline:hover .nav-action-external {
  color: #2a5298 !important;
}

/* Primary variant */
.nav-action-primary {
  color: white;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.15));
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.nav-action-primary:hover {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.25));
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

/* Secondary variant */
.nav-action-secondary {
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.nav-action-secondary:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-2px);
}

.nav-action:focus {
  outline: 2px solid rgba(255, 255, 255, 0.6);
  outline-offset: 2px;
}

/* Mobile styles */
@media (max-width: 991.98px) {
  .nav-action {
    width: 100%;
    justify-content: center;
    padding: 1rem 1.75rem;
    font-size: 0.875rem;
    margin: 0.25rem 0;
  }

  .nav-action:hover {
    transform: translateX(6px);
  }
}

/* Desktop styles */
@media (min-width: 992px) {
  .nav-action {
    padding: 0.5rem 1.125rem;
    font-size: 0.75rem;
    margin: 0;
  }
}
</style>

